import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

/**
 * Reusable Mapbox GL map — foundation for terrain analysis.
 * Token comes from REACT_APP_MAPBOX_TOKEN (public browser token) — never hardcoded.
 * center is [longitude, latitude]. enableTerrain adds the Mapbox raster-DEM 3D terrain
 * (visual relief; elevation sampling comes later via map.queryTerrainElevation).
 */
export function MapboxMap({
  center = [-106.0, 39.55], // central Rockies
  zoom = 9,
  pitch = 0,
  styleUrl = "mapbox://styles/mapbox/outdoors-v12",
  enableTerrain = false,
  terrainExaggeration = 1.0,  // 0 = flat render but DEM tiles still load (for analysis/heatmaps)
  height = 420,
  className = "",
  onLoad,
  testId = "mapbox-map",
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [loadError, setLoadError] = useState(null);
  const token = process.env.REACT_APP_MAPBOX_TOKEN;

  useEffect(() => {
    if (!token || mapRef.current) return undefined;
    mapboxgl.accessToken = token;
    let map;
    try {
      map = new mapboxgl.Map({
        container: containerRef.current,
        style: styleUrl,
        center,
        zoom,
        pitch,
        attributionControl: true,
      });
    } catch (e) {
      setLoadError("Map failed to initialize.");
      return undefined;
    }
    map.addControl(new mapboxgl.NavigationControl(), "top-right");
    map.on("error", (e) => {
      if (e?.error?.status === 401 || e?.error?.status === 403) setLoadError("Map token rejected — check REACT_APP_MAPBOX_TOKEN.");
    });
    map.on("load", () => {
      // Always load the DEM source (invisible) so queryTerrainElevation works for analysis
      // even in 2D; enableTerrain only controls the 3D visual relief.
      if (!map.getSource("mapbox-dem")) {
        map.addSource("mapbox-dem", {
          type: "raster-dem",
          url: "mapbox://mapbox.mapbox-terrain-dem-v1",
          tileSize: 512,
          maxzoom: 14,
        });
      }
      if (enableTerrain) {
        map.setTerrain({ source: "mapbox-dem", exaggeration: terrainExaggeration });
      }
      if (onLoad) onLoad(map);
    });
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, styleUrl, enableTerrain, pitch, terrainExaggeration]);

  if (!token) {
    return (
      <div className={`grid place-items-center border border-dashed border-saddle/30 bg-bone text-charcoal/55 text-sm ${className}`}
        style={{ height }} data-testid={`${testId}-missing-token`}>
        Map not configured — set REACT_APP_MAPBOX_TOKEN in frontend/.env
      </div>
    );
  }
  return (
    <div className={`relative ${className}`} data-testid={testId}>
      <div ref={containerRef} aria-label="Interactive map" style={{ width: "100%", height }} data-testid={`${testId}-canvas`} />
      {loadError && (
        <div className="absolute inset-0 grid place-items-center bg-paper/90 text-sm text-[#B04A3A] px-6 text-center" data-testid={`${testId}-error`}>{loadError}</div>
      )}
    </div>
  );
}
